import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { setCookie, getCookie } from './utils';
import { Chart } from "react-google-charts";

const brandname = "IndoorAirIQ";
const login_api_url = process.env.REACT_APP_LOGIN_API_URL;
const resource_api_url = process.env.REACT_APP_RESOURCE_API_URL;

// Room colors matching Stitch design
const ROOM_COLORS = {
    'Pepijn': '#00d4aa',
    'Simon': '#00b4d8',
    'Ouders': '#7b2ff7',
    'Evi': '#f59e0b',
};
const DEFAULT_COLOR = '#ef4444';

// Rooms to auto-load
const AUTO_LOAD_ROOMS = ['Pepijn', 'Simon', 'Ouders', 'Evi'];

function getRoomColor(name) {
    for (const [key, color] of Object.entries(ROOM_COLORS)) {
        if (name && name.toLowerCase().includes(key.toLowerCase())) return color;
    }
    return DEFAULT_COLOR;
}

function getRoomLabel(name) {
    for (const key of Object.keys(ROOM_COLORS)) {
        if (name && name.toLowerCase().includes(key.toLowerCase())) return key;
    }
    return name || 'Unknown';
}

// Generate date labels for the last 7 nights
function generateDateLabels() {
    const labels = [];
    for (let i = 7; i >= 1; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        labels.push(d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }));
    }
    return labels;
}

function App() {
    const [showGoogleLogin, setShowGoogleLogin] = useState(true);
    const [user, setUser] = useState(null);
    const [strava_data, setStrava_data] = useState(null);
    const [responseData, setResponseData] = useState({});
    const [loadingstatus, setLoadingstatus] = useState({});
    const [menu, setMenu] = useState(null);
    const [status, setStatus] = useState(null);
    const [showConnectButton, setShowConnectButton] = useState(false);

    // Chart data: array of { name, today, yesterday, last7 }
    const [roomData, setRoomData] = useState([]);
    const [autoLoaded, setAutoLoaded] = useState(false);
    const loadQueueRef = useRef([]);
    const [nightsToShow, setNightsToShow] = useState(7);

    // Debounced trend chart data - only renders after roomData stops changing for 1.5s
    const [trendChartData, setTrendChartData] = useState(null);
    useEffect(() => {
        if (roomData.length === 0) {
            setTrendChartData(null);
            return;
        }
        const timer = setTimeout(() => {
            const allDateLabels = generateDateLabels();
            const slicedLabels = allDateLabels.slice(7 - nightsToShow);
            const header = ["Day", ...roomData.map(r => getRoomLabel(r.name))];
            const rows = slicedLabels.map((label, i) => {
                const dataIndex = 7 - nightsToShow + i;
                return [label, ...roomData.map(r => {
                    const val = r.last7 && r.last7[dataIndex];
                    return typeof val === 'number' ? parseFloat(val.toFixed(0)) : 0;
                })];
            });
            const result = [header, ...rows];
            setTrendChartData(result);
        }, 500);
        return () => clearTimeout(timer);
    }, [roomData, nightsToShow]);

    // Build comparison data from roomData
    const comparisonData = useMemo(() => {
        return roomData.map(r => ({
            label: getRoomLabel(r.name),
            today: Math.round(r.today),
            yesterday: Math.round(r.yesterday),
            color: getRoomColor(r.name),
            delta: r.yesterday > 0 ? Math.round(((r.today - r.yesterday) / r.yesterday) * 100) : 0,
        }));
    }, [roomData]);

    // Comparison chart: min/max from today/yesterday values only
    const comparisonMin = useMemo(() => {
        if (roomData.length === 0) return 0;
        const vals = roomData.flatMap(r => [r.today, r.yesterday]).filter(v => v > 0);
        return vals.length > 0 ? Math.min(...vals) - 50 : 0;
    }, [roomData]);
    const comparisonMax = useMemo(() => {
        if (roomData.length === 0) return 1200;
        const vals = roomData.flatMap(r => [r.today, r.yesterday]).filter(v => v > 0);
        return vals.length > 0 ? Math.max(...vals) + 50 : 1200;
    }, [roomData]);

    // Trend chart: min from only the visible nights (respects slider)
    const trendMin = useMemo(() => {
        if (roomData.length === 0) return 0;
        const startIdx = 7 - nightsToShow;
        const vals = roomData.flatMap(r => (r.last7 || []).slice(startIdx)).filter(v => v > 0);
        return vals.length > 0 ? Math.min(...vals) - 50 : 0;
    }, [roomData, nightsToShow]);

    const trendChartOptions = {
        legend: { position: "bottom", textStyle: { color: '#a0aec0', fontSize: 11 } },
        backgroundColor: 'transparent',
        chartArea: { width: '85%', height: '65%' },
        hAxis: {
            textStyle: { color: '#a0aec0', fontSize: 11 },
        },
        vAxis: {
            textStyle: { color: '#64748b', fontSize: 10 },
            title: 'PPM',
            titleTextStyle: { color: '#64748b', italic: false, fontSize: 11 },
            viewWindow: { min: Math.max(trendMin, 0) },
        },
        colors: roomData.map(r => getRoomColor(r.name)),
        bar: { groupWidth: '65%' },
        isStacked: false,
    };

    function onClickHandler() {
        setMenu(
            <div className="auth-card">
                <div style={{ marginBottom: '20px' }}>
                    <i className="fas fa-spinner fa-spin" style={{ color: 'var(--airiq-teal)', fontSize: '24px' }}></i>
                </div>
                <p>After logging into Google, please wait a few seconds for this page to load</p>
                <div style={{ marginTop: '20px' }}>{googleLoginButton}</div>
            </div>
        );
    }

    const googleLoginButton = useMemo(() => {
        return (
            <GoogleLogin
                shape='pill'
                click_listener={() => onClickHandler()}
                onSuccess={credentialResponse => {
                    axios.post(`${login_api_url}/api/Login`, { test: 'test' }, {
                        headers: { Authorization: credentialResponse.credential }
                    })
                    .then((res) => {
                        setCookie(`${brandname}_token`, res.data.token, { path: '/', expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365) });
                        setShowGoogleLogin(false);
                        checkToken();
                    })
                    .catch((error) => console.error(error));
                }}
                onError={() => console.log('Login Failed')}
            />
        );
    }, []);

    const ConnectButton = useMemo(() => {
        return (
            <button className="btn-airiq" onClick={() => {
                const clientId = '602781446c33bc66cd1dc4c6';
                const redirectUri = `${resource_api_url}/api/Store_Tokens`;
                const scope = 'read_station';
                const state = getCookie(`${brandname}_token`);
                const authUrl = `https://api.netatmo.com/oauth2/authorize?state=${state}&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}`;
                window.open(authUrl, '_self');
            }}>
                <i className="fas fa-link" style={{ marginRight: '8px' }}></i>
                Connect to Netatmo
            </button>
        );
    }, []);

    function checkToken() {
        const cookieValue = getCookie(`${brandname}_token`);
        console.log('[DEBUG] checkToken called, cookie exists:', !!cookieValue);
        if (cookieValue) {
            axios.get(`${login_api_url}/api/CheckToken`, {
                headers: { Authorization: cookieValue }
            })
            .then(response => {
                console.log('[DEBUG] CheckToken response:', response.status, response.data);
                if (response.data.userid) {
                    setShowGoogleLogin(false);
                    setUser(response.data.userid);
                    setStatus(response.status);
                }
            })
            .catch(error => {
                console.log('[DEBUG] CheckToken error:', error.response?.status);
                setStatus(error.response.status);
            });
        } else {
            console.log('[DEBUG] No cookie, showing login');
            setStatus(401);
            setShowGoogleLogin(true);
        }
    }

    useEffect(() => {
        console.log('[DEBUG] Status useEffect triggered, status:', status);
        switch (status) {
            case 401:
                setMenu(
                    <div className="auth-card">
                        <div style={{ marginBottom: '16px' }}>
                            <i className="fas fa-wind" style={{ fontSize: '40px', color: 'var(--airiq-teal)' }}></i>
                        </div>
                        <h2>Welcome to IndoorAirIQ</h2>
                        <p>Sign in with Google to start monitoring your indoor air quality</p>
                        <div style={{ margin: '24px 0' }}>{googleLoginButton}</div>
                        <div className="auth-steps">
                            <div className="auth-step active">
                                <span className="step-number">1</span>
                                <span>Google Login</span>
                            </div>
                            <span className="auth-step-arrow">→</span>
                            <div className="auth-step">
                                <span className="step-number">2</span>
                                <span>Netatmo Auth</span>
                            </div>
                            <span className="auth-step-arrow">→</span>
                            <div className="auth-step">
                                <span className="step-number">3</span>
                                <span>View Data</span>
                            </div>
                        </div>
                    </div>
                );
                break;
            case 202:
                setMenu(
                    <div className="auth-card">
                        <div style={{ marginBottom: '16px' }}>
                            <i className="fas fa-spinner fa-spin" style={{ fontSize: '32px', color: 'var(--airiq-teal)' }}></i>
                        </div>
                        <h2>Authenticating...</h2>
                        <p>Checking if we have your Netatmo tokens.</p>
                    </div>
                );
                axios.get(`${resource_api_url}/api/User`, {
                    headers: { Authorization: getCookie(`${brandname}_token`) }
                })
                .then((res) => setStatus(res.status))
                .catch((err) => setStatus(err.response.status));
                break;
            case 204:
                setMenu(
                    <div className="auth-card">
                        <div style={{ marginBottom: '16px' }}>
                            <i className="fas fa-check-circle" style={{ fontSize: '32px', color: 'var(--airiq-teal)' }}></i>
                        </div>
                        <h2>Loading your data...</h2>
                        <p>Connected! Fetching your Netatmo stations.</p>
                    </div>
                );
                axios.get(`${resource_api_url}/api/Get_Activities`, {
                    headers: { Authorization: getCookie(`${brandname}_token`) }
                })
                .then((res) => {
                    console.log('[DEBUG] Get_Activities response:', res.status, 'devices:', res.data?.body?.devices?.length);
                    if (res.status === 200) {
                        setStrava_data(res.data.body.devices);
                        setMenu(null);
                    } else {
                        setStatus(res.status);
                    }
                })
                .catch((err) => setStatus(err.response.status));
                break;
            case 404:
                setMenu(
                    <div className="auth-card">
                        <div style={{ marginBottom: '16px' }}>
                            <i className="fas fa-plug" style={{ fontSize: '40px', color: 'var(--airiq-teal)' }}></i>
                        </div>
                        <h2>Connect to Netatmo</h2>
                        <p>
                            Connect your Netatmo account to view your PPM levels. We value your privacy — please read our{' '}
                            <a href="#/admin/Privacy">privacy policy</a> to learn what happens with your data.
                        </p>
                        <div style={{ margin: '24px 0' }}>{ConnectButton}</div>
                    </div>
                );
                break;
            case 440:
                setMenu(
                    <div className="auth-card">
                        <div style={{ marginBottom: '16px' }}>
                            <i className="fas fa-clock" style={{ fontSize: '40px', color: '#f59e0b' }}></i>
                        </div>
                        <h2>Session Expired</h2>
                        <p>Your session has expired. Please sign in again to continue.</p>
                        <div style={{ margin: '24px 0' }}>{googleLoginButton}</div>
                    </div>
                );
                setShowGoogleLogin(true);
                break;
            default:
                console.log('[DEBUG] Default case, calling checkToken');
                checkToken();
                break;
        }
    }, [status, googleLoginButton]);

    const handleButtonClick = useCallback((entry, module = null, type) => {
        const id = type === 'station' ? entry._id : module._id;
        setLoadingstatus(prev => ({ ...prev, [id]: 'loading' }));

        axios.post(`${resource_api_url}/api/Get_Activity`, {
            station_id: entry._id,
            module_id: module ? module._id : null,
            type: type
        }, {
            headers: { Authorization: getCookie(`${brandname}_token`) }
        })
        .then((res) => {
            const name = type === 'station' ? entry.station_name : module.module_name;
            const last7 = [...res.data.last_7_nights].reverse();

            setRoomData(prev => {
                // Replace if same name exists, else add
                const existing = prev.findIndex(r => r.name === name);
                const newEntry = { name, today: res.data.today, yesterday: res.data.yesterday, last7 };
                if (existing >= 0) {
                    const updated = [...prev];
                    updated[existing] = newEntry;
                    return updated;
                }
                return [...prev, newEntry];
            });

            setLoadingstatus(prev => ({ ...prev, [id]: 'loaded' }));
            setResponseData(prev => ({ ...prev, [id]: res.data }));
        })
        .catch((err) => {
            setLoadingstatus(prev => ({ ...prev, [id]: 'error' }));
            if (err.response && err.response.status === 401) setShowGoogleLogin(true);
            else if (err.response && err.response.status === 404) setShowConnectButton(true);
        });
    }, []);

    // Auto-load specific rooms when stations data becomes available
    useEffect(() => {
        if (strava_data && !autoLoaded) {
            setAutoLoaded(true);
            const toLoad = [];
            strava_data.forEach(entry => {
                AUTO_LOAD_ROOMS.forEach(room => {
                    if (entry.station_name && entry.station_name.toLowerCase().includes(room.toLowerCase())) {
                        toLoad.push({ entry, module: null, type: 'station' });
                    }
                });
                entry.modules.forEach(module => {
                    AUTO_LOAD_ROOMS.forEach(room => {
                        if (module.module_name && module.module_name.toLowerCase().includes(room.toLowerCase())) {
                            toLoad.push({ entry, module, type: 'module' });
                        }
                    });
                });
            });
            // Load sequentially with delay
            toLoad.forEach((item, index) => {
                setTimeout(() => {
                    handleButtonClick(item.entry, item.module, item.type);
                }, index * 800);
            });
        }
    }, [strava_data, autoLoaded, handleButtonClick]);

    // Comparison bar component
    const ComparisonBar = ({ label, today, yesterday, color, delta }) => {
        const scaleMin = Math.max(comparisonMin, 0);
        const range = comparisonMax - scaleMin;
        const todayPct = range > 0 ? ((today - scaleMin) / range) * 100 : 50;
        const yesterdayPct = range > 0 ? ((yesterday - scaleMin) / range) * 100 : 50;
        return (
            <div className="ppm-comparison-row">
                <div className="ppm-comparison-header">
                    <span className="ppm-room-label">{label}</span>
                    <span className={`ppm-delta ${delta > 0 ? 'delta-up' : delta < 0 ? 'delta-down' : 'delta-flat'}`}>
                        {delta > 0 ? '+' : ''}{delta}%
                        <i className={`fas ${delta > 0 ? 'fa-arrow-up' : delta < 0 ? 'fa-arrow-down' : 'fa-minus'}`}></i>
                    </span>
                </div>
                <div className="ppm-bars">
                    <div className="ppm-bar" style={{ width: `${Math.min(todayPct, 100)}%`, background: color }}>
                        <span className="ppm-bar-value">{today}</span>
                    </div>
                    <div className="ppm-bar ppm-bar-yesterday" style={{ width: `${Math.min(yesterdayPct, 100)}%`, background: color }}>
                        <span className="ppm-bar-value">{yesterday}</span>
                    </div>
                </div>
            </div>
        );
    };

    // Station card component
    const StationCard = ({ entry, module, type, color, index }) => {
        const id = type === 'station' ? entry._id : module._id;
        const name = type === 'station' ? entry.station_name : module.module_name;
        const label = getRoomLabel(name);
        const cardColor = color || getRoomColor(name);
        const status = loadingstatus[id];

        return (
            <div className="netatmo-station-card" style={{ borderLeftColor: cardColor + '80' }}>
                <div className="station-card-header">
                    <div className="station-icon" style={{ background: cardColor + '1a', color: cardColor }}>
                        <i className="fas fa-broadcast-tower"></i>
                    </div>
                    {status === 'loaded' && (
                        <span className="station-badge station-badge-loaded">
                            <i className="fas fa-check-circle"></i> Data loaded
                        </span>
                    )}
                    {status === 'loading' && (
                        <span className="station-badge station-badge-syncing">
                            <i className="fas fa-sync fa-spin"></i> Loading...
                        </span>
                    )}
                    {status === 'error' && (
                        <span className="station-badge station-badge-error">
                            <i className="fas fa-exclamation-circle"></i> Error
                        </span>
                    )}
                </div>
                <div className="station-card-info">
                    <h4 className="station-card-name">{label}</h4>
                    <p className="station-card-module">{name}</p>
                </div>
                <button
                    className={`station-card-btn ${status === 'loading' ? 'station-card-btn-disabled' : ''}`}
                    onClick={() => handleButtonClick(entry, module, type)}
                    disabled={status === 'loading'}
                    style={{ borderColor: cardColor + '30', color: status !== 'loading' ? cardColor : undefined }}
                >
                    <i className={status === 'loading' ? 'fas fa-sync fa-spin' : 'fas fa-sync-alt'}></i>
                    {status === 'loading' ? 'Loading...' : 'Get Data'}
                </button>
            </div>
        );
    };

    const cleardata = () => {
        setRoomData([]);
    };

    const hasData = roomData.length > 0;

    console.log('[DEBUG] Render - showGoogleLogin:', showGoogleLogin, 'user:', user, 'menu:', !!menu, 'strava_data:', !!strava_data, 'status:', status);

    return (
        <div className="content ppm-dashboard">
            {/* Auth / Menu */}
            {menu}

            {!showGoogleLogin && user && !menu && (
                <>
                    {/* Dashboard Header */}
                    <div className="ppm-header">
                        <p className="ppm-subtitle">VIEW YOUR PPM LEVELS</p>
                        <h2 className="ppm-title">Netatmo Intelligence Dashboard</h2>
                    </div>

                    {/* Charts Grid */}
                    <div className="ppm-charts-grid">
                        {/* Section 1: PPM Comparison */}
                        <div className="ppm-glass-card">
                            <div className="ppm-card-header">
                                <div>
                                    <h3 className="ppm-card-title">PPM Comparison: Today vs Yesterday</h3>
                                    <p className="ppm-card-sub">CO2 Concentration across main zones</p>
                                </div>
                                <div className="ppm-legend-inline">
                                    <span className="ppm-legend-dot" style={{ background: '#00d4aa' }}></span>
                                    <span className="ppm-legend-label">Today</span>
                                    <span className="ppm-legend-dot" style={{ background: '#00b4d8' }}></span>
                                    <span className="ppm-legend-label">Yesterday</span>
                                </div>
                            </div>
                            {hasData ? (
                                <div className="ppm-comparison-list">
                                    {comparisonData.map(r => (
                                        <ComparisonBar key={r.label} {...r} />
                                    ))}
                                </div>
                            ) : (
                                <div className="ppm-empty-state">
                                    <i className="fas fa-chart-bar"></i>
                                    <p>Loading room data...</p>
                                </div>
                            )}
                        </div>

                        {/* Section 2: 7-Night Trend */}
                        <div className="ppm-glass-card">
                            <div className="ppm-card-header">
                                <div>
                                    <h3 className="ppm-card-title">{nightsToShow}-Night PPM Trend</h3>
                                    <p className="ppm-card-sub">Aggregated multi-sensor weekly view</p>
                                </div>
                                <div className="ppm-slider-control">
                                    <span className="ppm-slider-label">Nights: {nightsToShow}</span>
                                    <input
                                        type="range"
                                        min="1"
                                        max="7"
                                        value={nightsToShow}
                                        onChange={(e) => setNightsToShow(parseInt(e.target.value))}
                                        className="ppm-slider"
                                    />
                                </div>
                            </div>
                            {trendChartData && (
                                <Chart
                                    chartType="ColumnChart"
                                    width="100%"
                                    height="300px"
                                    data={trendChartData}
                                    options={trendChartOptions}
                                />
                            )}
                            {!trendChartData && (
                                <div className="ppm-empty-state">
                                    <i className="fas fa-chart-line"></i>
                                    <p>Loading trend data...</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Section 3: Netatmo Stations */}
                    <div className="ppm-stations-section">
                        <div className="ppm-stations-header">
                            <h3 className="ppm-stations-title">Your Netatmo Stations</h3>
                            {strava_data && (
                                <span className="ppm-active-count">Active Devices: {strava_data.length}</span>
                            )}
                        </div>
                        <div className="ppm-stations-grid">
                            {strava_data && strava_data.map((entry, ei) => (
                                <React.Fragment key={entry._id}>
                                    <StationCard entry={entry} module={null} type="station" index={ei} />
                                    {entry.modules.map((module, mi) => (
                                        <StationCard
                                            key={module._id}
                                            entry={entry}
                                            module={module}
                                            type="module"
                                            index={ei * 10 + mi}
                                        />
                                    ))}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Clear Button */}
                    {hasData && (
                        <div style={{ textAlign: 'right', marginTop: '16px' }}>
                            <button className="btn-airiq-outline" onClick={cleardata}>
                                <i className="fas fa-redo" style={{ marginRight: '8px' }}></i>
                                Clear Charts
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default App;
